import { useEffect, useMemo, useState } from "react";
import type * as Y from "yjs";
import type { SolvePhase } from "@playbookai/types";
import { phaseAreasMap, readPhaseAreas } from "./phase-area-doc-shape.js";
import {
  layoutPhaseAreas,
  phaseAreaExtent,
  type PhaseAreaLayout,
} from "./phase-areas.js";

export interface SolvePhaseAreas {
  /** Rect per revealed phase, in reveal order. */
  readonly layout: PhaseAreaLayout;
  /** The canvas extent the areas occupy — feeds `<Canvas dimensions>`. */
  readonly extent: { readonly w: number; readonly h: number };
}

/**
 * Subscribe to the collaborative phase-area geometry.
 *
 * Deliberately a revision counter rather than derived state: `readPhaseAreas`
 * builds a fresh object each call, so storing it would re-render on every
 * unrelated doc event. The counter is the same `force((n) => n + 1)` idiom
 * `SolveMiniMap` and the DS `WidgetLayer` use for their own Yjs subscriptions.
 */
export function usePhaseAreas(
  doc: Y.Doc,
  phases: readonly SolvePhase[],
): SolvePhaseAreas {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const map = phaseAreasMap(doc);
    const observe = (): void => setRevision((n) => n + 1);
    map.observeDeep(observe);
    return (): void => map.unobserveDeep(observe);
  }, [doc]);

  // `revision` is the subscription trigger — the doc itself is read
  // imperatively, so it belongs in the dependency list even though the body
  // never reads it.
  return useMemo(() => {
    const layout = layoutPhaseAreas(phases, readPhaseAreas(doc));
    return { layout, extent: phaseAreaExtent(layout) };
  }, [doc, phases, revision]);
}
